import { DataTable } from "primereact/datatable";
import { useEffect, useState, Suspense, useRef } from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import AppLoading from '../app-loading/app-loading';
import orderServices from "../../services/orders/orderservice";
import { InputText } from 'primereact/inputtext';
import InvoicePayment from "../invoices/invoice-payment";
import { Toast } from 'primereact/toast';
import RouteConstant from "../../constants/route.constants";
import { useNavigate } from "react-router-dom";
import { GetWayLinkModel } from "../../models/getway-link/getway-link-model";
import { selectedGetway } from "../../redux/slices/getway-link";
import { useAppDispatch } from "../../redux/hooks";
import { OverlayPanel } from "primereact/overlaypanel";
import editUserServices, { FlagHistoryItem } from "../../services/edit-users/edit-users.service";
import { Paginator } from "primereact/paginator";
import './resend-payment-link-Datatable.css';
import { ColumnDef } from "../../models/orders/order-list-model";

interface DataTableProps {
  dataList: any[];
  onFlagCacheUpdate?: (cache: Record<number, FlagHistoryItem | null>) => void;
  onPaymentSuccess?: () => void;
}

const PaymentLinkListDataTable: React.FC<DataTableProps> = ({ dataList, onFlagCacheUpdate, onPaymentSuccess }) => {
  const [orderDataList, setOrderDataList] = useState<any>();
  const [visible, setVisible] = useState(false);
  const [rowData, setRowData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [paymentModeId, setPaymentModeId] = useState<any>('0');
  const toast = useRef<Toast>(null);
  const [value, setValue] = useState('');
  const dispatch = useAppDispatch();
  var navigate = useNavigate();

  // Paginator state
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  // ── In-header search filters ───────────────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState<Record<string, boolean>>({});
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({
    invoiceBy: '',
    customerName: '',
    primaryContactNo: '',
    invoiceNo: '',
    orderId: '',
  });

  const toggleSearch = (field: string) => {
    setSearchOpen(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const updateFilter = (field: string, val: string) => {
    setSearchFilters(prev => ({ ...prev, [field]: val }));
    setFirst(0); // reset to page 1 on filter change
  };

  // Apply all active filters to get filteredData
  const filteredData = (orderDataList ?? []).filter((row: any) => {
    if (searchFilters.invoiceBy && !String(row.invoiceBy ?? '').toLowerCase().includes(searchFilters.invoiceBy.toLowerCase())) return false;
    if (searchFilters.customerName && !String(row.customerName ?? '').toLowerCase().includes(searchFilters.customerName.toLowerCase())) return false;
    if (searchFilters.primaryContactNo && !String(row.primaryContactNo ?? '').includes(searchFilters.primaryContactNo)) return false;
    if (searchFilters.invoiceNo && !String(row.invoiceNo ?? '').toLowerCase().includes(searchFilters.invoiceNo.toLowerCase())) return false;
    if (searchFilters.orderId && !String(row.orderId ?? '').toLowerCase().includes(searchFilters.orderId.toLowerCase())) return false;
    return true;
  });

  const pagedData = filteredData.slice(first, first + rows);

  // \u2500\u2500 In-header search: icon-only circle, expands to input on click \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  const searchableHeader = (label: string, field: string, placeholder: string) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      width: '100%',
      minWidth: 0
    }}>
      {/* Top Row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '6px',
        width: '100%',
        minWidth: 0
      }}>
        {/* Label (takes remaining space safely) - allows wrapping */}
        <span style={{
          fontWeight: 600,
          overflow: 'visible',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.3,
          flex: 1,
          minWidth: 0
        }}>
          {label}
        </span>

        {/* Icons container (fixed width, prevents overlap) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0
        }}>
          {/* Search Icon */}
          <span
            onClick={(e) => { e.stopPropagation(); toggleSearch(field); }}
            title={searchFilters[field] ? `Active filter: "${searchFilters[field]}"` : `Search ${label}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              cursor: 'pointer',
              background: searchOpen[field] ? '#dcfce7' : searchFilters[field] ? '#bbf7d0' : 'transparent',
              border: `1.5px solid ${searchOpen[field] || searchFilters[field] ? '#22c55e' : '#d1d5db'}`,
              color: searchOpen[field] || searchFilters[field] ? '#16a34a' : '#9ca3af',
              transition: 'all 0.15s',
              position: 'relative',
              flexShrink: 0
            }}
          >
            <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
            {searchFilters[field] && !searchOpen[field] && (
              <span style={{
                position: 'absolute',
                top: -3,
                right: -3,
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#22c55e',
                border: '1.5px solid white',
              }} />
            )}
          </span>

          {/* Clear Icon */}
          {searchFilters[field] && (
            <i
              className="pi pi-times-circle"
              style={{
                fontSize: '0.8rem',
                cursor: 'pointer',
                color: '#ef4444',
                flexShrink: 0
              }}
              onClick={(e) => {
                e.stopPropagation();
                updateFilter(field, '');
                setSearchOpen(prev => ({ ...prev, [field]: false }));
              }}
              title="Clear filter"
            />
          )}
        </div>
      </div>

      {/* Search Input */}
      {searchOpen[field] && (
        <input
          autoFocus
          value={searchFilters[field]}
          onChange={e => updateFilter(field, e.target.value)}
          onClick={e => e.stopPropagation()}
          placeholder={placeholder}
          style={{
            width: '100%',
            fontSize: '0.76rem',
            padding: '4px 8px',
            border: '1.5px solid #22c55e',
            borderRadius: '6px',
            outline: 'none',
            boxSizing: 'border-box',
            boxShadow: '0 0 0 2px rgba(34,197,94,0.12)',
          }}
        />
      )}
    </div>
  );

  // ── Flag overlay state ─────────────────────────────────────────────────────
  const flagOP = useRef<OverlayPanel>(null);
  // Store full history array per customer: [0]=newest, [last]=oldest(creator)
  const [flagCache, setFlagCache] = useState<Record<number, FlagHistoryItem[] | null>>({});
  const [activeHistory, setActiveHistory] = useState<FlagHistoryItem[] | null>(null);
  const [flagLoading, setFlagLoading] = useState(false);


  useEffect(() => {
    setOrderDataList(dataList);
  }, [dataList]);

  // Pre-fetch flag colours for all rows on page load
  useEffect(() => {
    if (!orderDataList || orderDataList.length === 0) return;
    const uniqueIds: number[] = [...new Set<number>(orderDataList.map((r: any) => r.customerId as number))];
    uniqueIds.forEach(async (id) => {
      setFlagCache(prev => {
        if (prev[id] !== undefined) return prev;
        return { ...prev, [id]: null };
      });
      try {
        const history = await editUserServices.getFlagHistory(id);
        const arr = history.length > 0 ? history : null;
        setFlagCache(prev => {
          const updated = { ...prev, [id]: arr };
          // Pass full array map to parent — AccountReceivableExport now accepts FlagHistoryItem[] per entry
          if (onFlagCacheUpdate) {
            onFlagCacheUpdate(updated as any);
          }
          return updated;
        });
      } catch {
        // leave null if fetch fails
      }
    });
  }, [orderDataList]);

  // ── Flag helpers ───────────────────────────────────────────────────────────
  const formatDate = (value: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const openFlagOverlay = async (e: React.MouseEvent, row: any) => {
    const id: number = row.customerId;
    flagOP.current?.toggle(e);

    if (flagCache[id] !== undefined) {
      setActiveHistory(flagCache[id]);
      return;
    }

    setFlagLoading(true);
    setActiveHistory(null);
    try {
      const history = await editUserServices.getFlagHistory(id);
      const arr = history.length > 0 ? history : null;
      setFlagCache(prev => ({ ...prev, [id]: arr }));
      setActiveHistory(arr);
    } finally {
      setFlagLoading(false);
    }
  };

  // SVG circle with white checkmark — matches users-datatable flag icon
  const FlagBadge = ({ flagCode }: { flagCode: number }) => {
    const color = flagCode === 1 ? '#ef4444' : flagCode === 2 ? '#3b82f6' : '#d1d5db';
    const title = flagCode === 1 ? 'Red Flag' : flagCode === 2 ? 'Blue Flag' : 'No Flag';
    return (
      <span title={title} style={{ display: 'inline-flex', transition: 'opacity 0.15s', opacity: flagCode === 0 ? 0.35 : 1 }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="11" cy="11" r="11" fill={color} />
          <path d="M6.5 11.5L9.5 14.5L15.5 8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  };

  const flagBodyTemplate = (row: any) => {
    const cached = flagCache[row.customerId];
    // cached is now FlagHistoryItem[] | null | undefined
    const arr = cached as FlagHistoryItem[] | null | undefined;
    const flagCode = arr && arr.length > 0 ? (arr[0]?.flagCode ?? 0) : 0;
    return (
      <span
        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        onClick={(e) => openFlagOverlay(e, row)}
        title="Click to view flag details"
      >
        <FlagBadge flagCode={flagCode} />
        <i className="pi pi-info-circle" style={{ fontSize: '0.78rem', color: '#9ca3af', marginLeft: '2px' }} />
      </span>
    );
  };


  function handlePaymentLink(row: any) {
    setVisible(true);
    setRowData(row)
  }

  // WhatsApp reminder — sends a collection reminder via WhatsApp template msg
  const handleWhatsappReminder = async (row: any) => {
    setLoading(true);
    try {
      await orderServices.sendWhatsAppTemplateMessage(row.orderId, "collection_report_msg_reminder");
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Reminder sent successfully', life: 3000 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to send reminder', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const mapAdditionalInfo = (data: any): GetWayLinkModel => {
    return {
      paymentURL: data.paymentURL,
      expectedDeliveryDate: data.expectedDeliveryDate,
    };
  };

  const columns: ColumnDef[] = [
    {
      header: "Receive Payment",
      cell: (row: any) => (
        <Button
          icon="pi pi-link"
          onClick={() => handlePaymentLink(row)}
          style={{ border: 'none', color: 'tomato', background: 'none' }}
          className="p-button-rounded"
          tooltip="Send Link"
        />)
    },
    {
      field: "lastLinkDate",
      header: "Last Sent Date",
      style: { minWidth: "9rem" },
      sortable: true,
      cell: (row: any) =>
        row.lastLinkDate != null ?
          new Date(row.lastLinkDate).toLocaleDateString("en-gb", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }) : "",
    },
    {
      field: "invoiceNo",
      header: "Invoice #",
      style: { minWidth: "5rem" },
      sortable: true,
    },
    {
      field: "invoiceDate",
      header: "Invoice Date",
      style: { minWidth: "9rem" },
      sortable: true,
      cell: (row: any) =>
        new Date(row.invoiceDate).toLocaleDateString("en-gb", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      field: "invoiceBy",
      header: "Invoice By",
      style: { minWidth: "9rem" },
      sortable: true,
    },
    {
      field: "ageDate",
      header: "Ageing",
      style: { minWidth: "9rem" },
      sortable: true,
    },
    {
      field: "grossAmount",
      header: "Gross Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.grossAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "rewardAmount",
      header: "Reward Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.rewardAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "finalAmount",
      header: "Final Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.finalAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "customerName",
      header: "Customer Name",
      style: { minWidth: "9rem" },
      sortable: true,
    },
    {
      field: "primaryContactNo",
      header: "Customer No#",
      style: { minWidth: "5rem" },
      sortable: true,
    },
  ];


  const hideDialog = () => {
    setVisible(false);
  };

  function handelSubmit() {
    if (paymentModeId === '0' || paymentModeId === 5) {
      toast.current?.show({
        severity: 'info',
        summary: 'Info',
        detail: 'Please select the payment mode.',
        life: 3000
      });
    }
    else {
      var paradata = {
        "orderId": rowData.orderId,
        "customerId": rowData.customerId,
        "customerName": rowData.customerName,
        "primaryContactNo": value !== "" ? value : rowData.primaryContactNo,
        "grossAmount": rowData.grossAmount,
        "paymentModeId": paymentModeId,
        "invoiceId": rowData.invoiceId,
        "customerEmail": rowData.customerEmail
      }
      orderServices.updateActualpayment(paradata).then((response: any) => {
        if (response.isSuccess) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: (paymentModeId === '6' || paymentModeId === 6) ? "Payment Link Send Success" : "Payment Successful",
            life: 3000
          });

          // 1. Remove the row from local list for immediate UI feedback
          setOrderDataList((prev: any[]) => prev.filter((row: any) => row.orderId !== rowData.orderId));

          // 2. Trigger parent metrics refresh
          if (onPaymentSuccess) {
            onPaymentSuccess();
          }

          // 3. Handle specific navigation or UI closure
          if (paymentModeId === "7") {
            const getwayData = response.additionalInfo;
            dispatch(selectedGetway(mapAdditionalInfo(getwayData)));
            navigate(RouteConstant.getwayProcess);
          } else {
            // For Cash (10), COD (1), Payment Link (6), etc.
            setVisible(false);
          }
        }
      })
    }
  }

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} />
        <div className="col-12 card border-noround p-2" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* DataTable with CSS-sticky left columns + scoped header fixes */}
          <div className="payment-link-table" style={{ overflowX: 'auto', flex: 1, minHeight: 0 }}>
            <DataTable
              value={pagedData ?? []}
              stripedRows
              showGridlines
              sortMode="multiple"
              emptyMessage="No Record found."
              tableStyle={{ minWidth: '160rem', tableLayout: 'auto', borderCollapse: 'collapse' }}
            >
              {/* ── CSS-sticky LEFT columns (exact px widths + cumulative left offsets) ── */}
              {/* Flag: 100px | Receive Payment: 160px | Payment Link Sent Date: 200px | Invoice #: 180px | Order ID: 140px */}
              <Column
                header="Flag"
                body={flagBodyTemplate}
                bodyClassName="ar-frozen-col"
                className="ar-frozen-header"
                style={{ width: '100px', textAlign: 'center', position: 'sticky', left: 0, zIndex: 1 }}
                headerStyle={{ textAlign: 'center', position: 'sticky', left: 0, zIndex: 2 }}
              />
              <Column
                header="Receive Payment"
                bodyClassName="ar-frozen-col"
                className="ar-frozen-header"
                style={{ width: '190px', textAlign: 'center', position: 'sticky', left: '100px', zIndex: 1 }}
                headerStyle={{ textAlign: 'center', position: 'sticky', left: '100px', zIndex: 2 }}
                body={(row: any) => (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button
                      icon="pi pi-link"
                      onClick={() => handlePaymentLink(row)}
                      style={{ border: 'none', color: 'tomato', background: 'none' }}
                      className="p-button-rounded"
                      tooltip="Send Link"
                    />
                    <Button
                      icon="pi pi-whatsapp"
                      onClick={() => handleWhatsappReminder(row)}
                      style={{ border: 'none', color: '#25D366', background: 'none' }}
                      className="p-button-rounded"
                      tooltip="Send WhatsApp Reminder"
                    />
                  </div>
                )}
              />
              <Column
                field="lastLinkDate"
                header="Payment Link Sent Date"
                sortable
                bodyClassName="ar-frozen-col"
                className="ar-frozen-header"
                style={{ width: '240px', position: 'sticky', left: '290px', zIndex: 1 }}
                headerStyle={{ position: 'sticky', left: '290px', zIndex: 2 }}
                body={(row: any) => row.lastLinkDate != null
                  ? new Date(row.lastLinkDate).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' })
                  : ''}
              />
              <Column
                field="invoiceNo"
                header={searchableHeader('Invoice #', 'invoiceNo', 'Search by invoice...')}
                sortable
                bodyClassName="ar-frozen-col"
                className="ar-frozen-header"
                style={{ width: '200px', position: 'sticky', left: '530px', zIndex: 1 }}
                headerStyle={{ position: 'sticky', left: '530px', zIndex: 2 }}
              />
              <Column
                field="orderId"
                header={searchableHeader('Order ID', 'orderId', 'Search by order...')}
                sortable
                bodyClassName="ar-frozen-col ar-frozen-last"
                className="ar-frozen-header ar-frozen-last"
                style={{ width: '180px', position: 'sticky', left: '730px', zIndex: 1 }}
                headerStyle={{ position: 'sticky', left: '730px', zIndex: 2 }}
              />

              <Column
                field="invoiceDate"
                header="Invoice Date"
                sortable
                style={{ width: '140px', whiteSpace: 'nowrap' }}
                body={(row: any) => new Date(row.invoiceDate).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' })}
              />
              <Column
                field="invoiceBy"
                header={searchableHeader('Invoice By', 'invoiceBy', 'Search by name...')}
                sortable
                style={{ width: '160px', whiteSpace: 'nowrap' }}
              />
              <Column
                field="ageDate"
                header="Ageing"
                sortable
                style={{ width: '120px', whiteSpace: 'nowrap' }}
              />
              <Column
                field="grossAmount"
                header="Gross Amount"
                sortable
                style={{ width: '140px', whiteSpace: 'nowrap' }}
                body={(row: any) => Math.round(row.grossAmount).toLocaleString('en-gb', { style: 'currency', currency: 'INR' })}
              />            
               <Column
                field="rewardAmount"
                header="Reward Amount"
                sortable
                style={{ width: '150px', whiteSpace: 'normal' }}
                body={(row: any) => Math.round(row.rewardAmount).toLocaleString('en-gb', { style: 'currency', currency: 'INR' })}
              />
               <Column
                field="finalAmount"
                header="Final Amount"
                sortable
                style={{ width: '140px', whiteSpace: 'normal' }}
                body={(row: any) => Math.round(row.finalAmount).toLocaleString('en-gb', { style: 'currency', currency: 'INR' })}
              />
              <Column
                field="customerName"
                header={searchableHeader('Customer Name', 'customerName', 'Search by name...')}
                sortable
                style={{ width: '200px', whiteSpace: 'normal' }}
              />
              <Column
                field="primaryContactNo"
                header={searchableHeader('Customer No#', 'primaryContactNo', 'Search by phone...')}
                sortable
                style={{ width: '160px', whiteSpace: 'nowrap' }}
              />
            </DataTable>
          </div>

          {/* Sticky paginator footer */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            background: 'white',
            borderTop: '1px solid #e5e7eb',
            zIndex: 10,
            flexShrink: 0,
          }}>
            <Paginator
              first={first}
              rows={rows}
              totalRecords={filteredData.length}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              onPageChange={onPageChange}
              template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
            />
          </div>

          {/* Flag history overlay — full audit timeline */}
          <OverlayPanel ref={flagOP} style={{ width: '420px' }}>
            {flagLoading ? (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem', color: '#22c55e' }} />
              </div>
            ) : !activeHistory || activeHistory.length === 0 ? (
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>No flag assigned to this customer.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <div style={{ marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <i className="pi pi-history" style={{ marginRight: '5px', color: '#22c55e' }} />
                    Flag Change History
                  </span>
                </div>

                {/* Latest Update Card — only most recent change */}
                {(() => {
                  const sorted = [...activeHistory].sort((a, b) => {
                    const da = a.updateDate ? new Date(a.updateDate).getTime() : 0;
                    const db = b.updateDate ? new Date(b.updateDate).getTime() : 0;
                    return db - da; // newest first
                  });

                  // The latest record is the most recently modified flag entry
                  const latest = sorted[0];

                  if (!latest) return null;

                  const flagColor =
                    latest.flagCode === 1 ? { bg: '#fecdd3', border: '#f87171', text: '#b91c1c', label: '🔴 Red Flag' } :
                      latest.flagCode === 2 ? { bg: '#bfdbfe', border: '#60a5fa', text: '#1d4ed8', label: '🔵 Blue Flag' } :
                        { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280', label: '⬜ No Flag' };

                  return (
                    <div style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      padding: '0.85rem',
                      position: 'relative',
                    }}>
                      {/* Latest badge */}
                      <span style={{
                        position: 'absolute', top: 6, right: 8,
                        fontSize: '0.58rem', background: '#22c55e', color: 'white',
                        borderRadius: '4px', padding: '2px 6px', fontWeight: 700,
                      }}>LATEST</span>

                      {/* Flag pill + Updated badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{
                          background: flagColor.bg, color: flagColor.text,
                          border: `1px solid ${flagColor.border}`,
                          borderRadius: '10px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600,
                        }}>
                          {flagColor.label}
                        </span>
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                          color: '#b45309', background: '#fef3c7',
                          borderRadius: '6px', padding: '2px 6px',
                        }}>✎ Updated By</span>
                      </div>

                      {/* Name */}
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                        {latest.updatedByName ?? '—'}
                      </div>

                      {/* Date */}
                      {latest.updateDate && (
                        <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>
                          {new Date(latest.updateDate).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {' '}
                          {new Date(latest.updateDate).toLocaleTimeString('en-gb', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}

                      {/* Comment */}
                      {latest.comments ? (
                        <div style={{
                          fontSize: '0.75rem', color: '#374151', marginTop: '6px',
                          fontStyle: 'italic', padding: '4px 6px', background: '#f9fafb', borderRadius: '4px'
                        }}>
                          "{latest.comments}"
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '6px', fontStyle: 'italic' }}>
                          No comment provided
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Created By Info - Footer */}
                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '0.75rem',
                  paddingTop: '0.75rem',
                  flexShrink: 0
                }}>
                  <div style={{
                    fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <i className="pi pi-user" style={{ fontSize: '0.7rem' }} />
                    <span><strong>Created by:</strong> {activeHistory?.[0]?.createdByName ? `${activeHistory[0].createdByName}` : 'System'}</span>
                  </div>
                  {activeHistory?.[0]?.createdDate && (
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px', marginLeft: '20px' }}>
                      {new Date(activeHistory[0].createdDate).toLocaleDateString('en-gb', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' '}
                      {new Date(activeHistory[0].createdDate).toLocaleTimeString('en-gb', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </OverlayPanel>
          <Dialog
            visible={visible}
            style={{ width: "67vw" }}
            header={"Select payment mode"}
            onHide={hideDialog}
          >
            <div className="col-12">
              <div className="pl-3 mb-3">
                <InvoicePayment updatePaymentId={setPaymentModeId} /><br />
              </div>
              {
                paymentModeId === "6" ?
                  <div className="col-12 card border-noround p-2">
                    <div className="col-12">
                      <div className="flex justify-content-start mb-2">
                        <div className="flex align-items-center gap-2 p-2 border-round"
                          style={{ backgroundColor: 'var(--blue-50)', border: '1px solid var(--blue-100)', display: 'inline-flex', maxWidth: 'fit-content' }}>
                          <i className="pi pi-info-circle" style={{ color: 'var(--blue-600)', fontSize: '1.25rem' }} />
                          <span style={{ color: 'var(--blue-700)' }}>if you leave the phone number blank, the payment link will automatically be sent to the primary contact number.</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="flex-auto">
                        <label htmlFor="number" className="font-bold block mb-2">
                          Phone Number
                        </label>
                        <InputText value={value} id="number" keyfilter="num" style={{ border: '1px solid #ced4da', borderRadius: '4px' }} onChange={(e) => setValue(e.target.value)} />
                      </div>
                    </div>
                  </div> : ""
              }
              <div className="col-12">
                <Button onClick={handelSubmit}>Submit</Button>
              </div>
            </div>
          </Dialog>
        </div>
        {loading && <AppLoading />}
      </Suspense>
    </>
  );
};

export default PaymentLinkListDataTable;